import { defineStore } from "pinia";
import { useUIStore } from "./ui.store";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { guestStatus, LangType, OldReservationImportLookupField, OptionChoice, type Guest, type ReservationSource } from "../helpers/types";
import { useI18n } from "vue-i18n";

const baseUrl = `/api/stays`;
const baseUrlSend = import.meta.env.VITE_APP_API_URL
  ? `${import.meta.env.VITE_APP_API_URL}/api/stays`
  : "/api/stays";

interface StaysStoreParams {
  lastReservationData?: Guest | null | undefined;
  lastCompanyData?: any | null | undefined;
}

export const useStaysStore = defineStore({
  id: "stays",
  state: () =>
  ({
    lastReservationData: null,
    lastCompanyData: null,
  } as StaysStoreParams),
  actions: {
    async getReservations(params?: {
      search?: string;
      page?: number | string;
      perPage?: number | string;
      approvalHistory?: number;
      filledHistory?: number;
      pendingHistory?: number;
      status?: string | number | null | undefined;
      statuses?: (string | number)[] | null | undefined;
      isLastApprovedReservations?: boolean;
      filterOptions?: object;
      waitingSelectResponsibleUser?: boolean;
      isSearchFromOthers?: boolean;
      reservationCode?: string;
    }) {
      const uiStore = useUIStore();
      // uiStore.startLoading();
      return fetchWrapper
        .get(`${baseUrl}/reservations`, params)
        .then(({ items, total, lastPage, statistics }) => {
          return {
            items,
            total,
            lastPage,
            statistics,
          };
        })
        .catch((error) => {
          console.log("Error in getReservations:", error);
          uiStore.showMessage("error", error.message || error);
          throw error;
        })
        .finally(async () => {
          // uiStore.stopLoading();
        });
    },

    async updateResponsibleUser(
      reservationId: string | number,
      responsibleUserId: string | number,
      reason: string,
      isCheckoutResponsibleUser?: boolean
    ) {
      const uiStore = useUIStore();
      const requestBody = { reservationId, responsibleUserId, reason, isCheckoutResponsibleUser };

      return fetchWrapper
        .put(`${baseUrl}/reservations/responsible-user`, requestBody)
        .then((response) => {
          uiStore.showMessage(
            "success",
            "Usuário responsável atualizado com sucesso!"
          );
          return response;
        })
        .catch((error) => {
          console.error("Erro ao atualizar usuário responsável:", error);
          uiStore.showMessage(
            "error",
            error.message || "Erro ao atualizar o responsável."
          );
          throw error;
        });
    },


    async getHistory(body: { cpf?: string; document?: string, guestId: number }) {
      const uiStore = useUIStore();
      // uiStore.startLoading();
      return fetchWrapper
        .get(`${baseUrl}/reservations/history`, body)
        .then((res) => res)
        .catch((error) => {
          console.log("Error in getReservations:", error);
          uiStore.showMessage("error", error.message || error);
          throw error;
        })
        .finally(async () => {
          // uiStore.stopLoading();
        });
    },
    async getHistoryForm(body: { cpf?: string; document?: string, guestId: number, friendlyCode?:string, residenceId?:string, source?:ReservationSource }) {
      return fetchWrapper
        .get(`${baseUrl}/reservations/history-form`, body)
        .then((res) => res)
        .catch((error) => {
          console.log("Error in getReservations:", error);
          throw error;
        });
    },

    async checkCautionStatus(id: string) {
      const response = await fetchWrapper.get(`/api/stays/check-caution-status/${id}`);
      return response;
    },

    async generateCodeToImportPreviousData(selectedGuestId: number ,language: LangType) {
      try {
          const response = await fetchWrapper.post(
          `${baseUrl}/reservations/generate-code-previous-data`,
            { lang: language, selectedGuestId: selectedGuestId }
          );
          return response;
       } catch (error: any) {
          throw error;
       }
   },

    async refuseReservation({
      id,
      refuseMotive,
    }: {
      id: string | number;
      refuseMotive: string;
    }) {
      const requestBody = {
        id,
        refuseMotive,
      };
      console.log(
        "Sending request to refuse guest form with body:",
        requestBody
      );
      return fetchWrapper
        .put(`${baseUrl}/refuseForm`, requestBody)
        .then((response) => response); // Não usar o catch aqui para o erro estourar para quem o chamou
    },
    async cancelReservation({
      id,
      cancelMotive,
      userId,
    }: {
      id: string | number;
      cancelMotive: string;
      userId: number;
    }) {
      const requestBody = {
        id,
        cancelMotive,
        userId,
      };
      console.log(
        "Sending request to refuse guest form with body:",
        requestBody
      );
      return fetchWrapper
        .put(`${baseUrl}/cancelForm`, requestBody)
        .then((response) => response); // Não usar o catch aqui para o erro estourar para quem o chamou
    },

    async changeFormVisibility({
      id,
      hiddenMotive,
    }: {
      id: string | number;
      hiddenMotive: string | null;
    }) {
      const requestBody = {
        id,
        hiddenMotive,
      };
      return fetchWrapper
        .put(`${baseUrl}/setFormVisibility`, requestBody)
        .then((response) => response);
    },

    async getGuestForm(id: string, source?: ReservationSource, friendlyCode?: string | number | null | undefined, t?: any) {
      // Agora usa o ID
      const uiStore = useUIStore();
      console.log("Sending request to get guest form with ID getGuest:", id);
      return fetchWrapper
        .get(`${baseUrl}/guestForm/${id}`, undefined, {
          source,
          ...(friendlyCode ? { friendlycode: friendlyCode } : {})
        })
        .then((response) => {
          console.log("API Response:", response);
          this.lastReservationData = response.data;
          return response.data; // Garantindo que estamos retornando os dados
        })
        .catch((error) => {
          console.error("Error in getGuestForm:", error);
          throw error; // Lança o erro para ser tratado onde a função é chamada
        });
    },

    async getGenericFormGuest(friendlyCode: string, residenceId: string, source: ReservationSource) {
      console.log("Sending request to get generic form guest:", { friendlyCode, residenceId, source });
      return fetchWrapper
        .get(`${baseUrl}/generic/guestForm/${friendlyCode}/${residenceId}/${source}`)
        .then((response) => {
          console.log("API Response for generic form:", response);
          this.lastReservationData = response;
          return response;
        })
        .catch((error) => {
          console.error("Error in getGenericFormGuest:", error);
          throw error;
        });
    },

    async sendGuestForm(
      id: string | undefined,
      formData: FormData,
      adminMode?: boolean,
      source?: ReservationSource,
      friendlyCode?: string | number | null | undefined,
    ) {

      try {

        console.log('formData at sendGuestForm:', formData);

        const uiStore = useUIStore();
        // Agora usa o ID
        console.log("Initial body:", formData);
        console.log(
          "FormData entries before send:",
          Array.from(formData.entries())
        );

        const url = adminMode
          ? `/api/stays/guestForm-admin/${id}`
          : `/api/stays/guestForm/${id}`;

        const response = await fetchWrapper
          .put(url, formData, {
            source,
            ...(friendlyCode ? { friendlycode: friendlyCode } : {}),
          });

        console.log("API Response:", response);
        if (response?.guestPassCode) {
          if (response?.guestPassCode.success) {
            uiStore.showMessage(
              "success",
              `Código da fechadura teve seu tempo atualizado!`
            );
          } else if (!response?.guestPassCode.success)
            uiStore.showMessage(
              "error",
              response?.guestPassCode.message ||
              "Erro ao atualizar o tempo do código da fechadura."
            );
        }
        return response;
      } catch (error) {
        console.error("API Error:", error);
        throw error;

      }
    },

    async createGuestFromGenericFormLink(
      friendlyCode: string,
      residenceId: string,
      source: ReservationSource,
      formData: FormData
    ) {
      try {
        const url = `${baseUrl}/generic/guestForm/${friendlyCode}/${residenceId}/${source}`;

        const response = await fetchWrapper.post(url, formData, {});

        console.log("API Response for generic form creation:", response);

        return response;
      } catch (error) {
        console.error("API Error:", error);
        throw error;
      }
    },
    async fetchLocalReservationDetails(reservationId: string) {
      try {
        const response = await fetchWrapper.get(
          `${baseUrl}/local/${reservationId}`
        );
        console.log("reservationId - ", reservationId);
        return response;
      } catch (error) {
        console.error("Error fetching local reservation details:", error);
        throw error;
      }
    },
    async approveReservation(reservationId: number, confirm = OptionChoice.NO) {
      return await fetchWrapper
        .post(`/api/stays/approve-reservation/${reservationId}`, {
          confirm,
        })
        .then((response) => response);
    },

    // async validateReservationForAction(reservationId: number) {
    //   return await fetchWrapper
    //     .post(`/api/stays/validate-reservation-for-action/${reservationId}`)
    //     .then((response) => response);
    // },

    async finishCheckout(reservationId: number, confirm = OptionChoice.NO) {
      return await fetchWrapper
        .post(`/api/stays/finish-checkout/${reservationId}`, {
          confirm,
        })
        .then((response) => response);
    },

    async revertCancellation(reservationId: number, confirm = OptionChoice.NO) {
      return fetchWrapper
        .put(`${baseUrl}/revertCancelForm`, { id: reservationId, confirm })
        .then((response) => response);
    },

    async processNotification(token: string) {
      try {
        const response = await fetchWrapper.post(
          `${baseUrl}/process-notification`,
          { token }
        );
        return response;
      } catch (error) {
        throw error;
      }
    },

    async importLastReservationDetails(reservationId: number, doc: string, docType: OldReservationImportLookupField, source?: ReservationSource) {
      try {
        const response = await fetchWrapper.post(`${baseUrl}/reservations/import-last-reservation/${reservationId}`, { doc, docType, source });
        return response;
      } catch (error) {
        console.error("Error importing last reservation details:", error);
        throw error;
      }
    },

    async removeSensitiveData(guestId: number | string) {
      const uiStore = useUIStore();
      try {
        const response = await fetchWrapper.put(`${baseUrl}/remove-sensitive-data/${guestId}`);
        uiStore.showMessage("success", response?.message || "Dados sensíveis removidos com sucesso!");
        return response;
      } catch (error: any) {
        console.error("Erro ao remover dados sensíveis:", error);
        uiStore.showMessage("error", error?.message || "Erro ao remover dados sensíveis.");
        throw error;
      }
    },

    async updateDynamicPassword(
      reservationId: number,
      dynamicPassword: string
    ) {
      const uiStore = useUIStore();
      try {
        uiStore.startLoading();
        const response = await fetchWrapper
          .post(`${baseUrl}/update-dynamic-password/${reservationId}`, {
            dynamicPassword,
          })
          .then((response) => response);
        if (!response.success) {
          uiStore.showMessage(
            "error",
            response.message || "Erro ao atualizar a senha dinâmica."
          );
        } else {
          uiStore.showMessage(
            "success",
            "Senha dinâmica atualizada com sucesso!"
          );
        }
        return response;
      } catch (error) {
        console.error("Erro ao atualizar a senha dinâmica:", error);
        throw error;
      } finally {
        uiStore.stopLoading();
      }
    },

    async changeCheckoutTime(
      guest: Guest,
      newCheckInTime: string,
      newCheckoutTime: string,
      confirm = OptionChoice.NO
    ) {
      const uiStore = useUIStore();

      try {
        uiStore.startLoading();
        const response = await fetchWrapper
          .put(`/api/stays/extendCheckoutForm`, {
            id: guest.id,
            checkout: newCheckoutTime,
            CheckIn: newCheckInTime,
            confirm
          })
          .then((response) => response);
        if (!response.success) {
          uiStore.showMessage(
            "error",
            response.message || "Erro ao atualizar horários."
          );
        } else {
          uiStore.showMessage("success", "Horários atualizados com sucesso!");
          if (response?.newPasscode?.success) {
            uiStore.showMessage(
              "success",
              `Nova senha TTLock atualizada com sucesso!`
            );
          } else if (
            response?.newPasscode?.success == false &&
            guest.residence?.usesTTLock
          ) {
            uiStore.showMessage(
              "error",
              `Erro ao atualizar a senha TTLock. ${response?.newPasscode?.message || ""
              }`
            );
          }
        }
        return response;
      } catch (error: any) {
        throw error;
      } finally {
        uiStore.stopLoading();
      }
    },

    async updateMaxGuests(
      guest: Guest,
      maxGuests: number,
    ) {
      const uiStore = useUIStore();

      try {
        uiStore.startLoading();
        const response = await fetchWrapper
          .put(`/api/stays/updateMaxGuests`, {
            id: guest.id,
            maxGuests
          })
          .then((response) => response);
        if (!response.success) {
          uiStore.showMessage(
            "error",
            response.message || "Erro ao atualizar o número máximo de hóspedes."
          );
        } else {
          uiStore.showMessage("success", "Número máximo de hóspedes atualizado com sucesso!");
        }
        return response;
      } catch (error: any) {
        throw error;
      } finally {
        uiStore.stopLoading();
      }
    },

    async getCompanyInfo(friendlyCode: string) {
      console.log("Sending request to get company info with friendlyCode:", friendlyCode);
      return fetchWrapper
        .get(`${baseUrl}/company-info/${friendlyCode}`)
        .then((response) => {
          console.log("Company Info API Response:", response);
          this.lastCompanyData = response.data;
          return response.data;
        })
        .catch((error) => {
          console.error("Error in getCompanyInfo:", error);
          throw error;
        });
      },
    async updateReservationStatusToValidate(
      guest: Guest,
    ) {
      const uiStore = useUIStore();

      try {
        uiStore.startLoading();
        const response = await fetchWrapper
          .post(`/api/stays/guest-status/${guest.id}`, {
            status : guestStatus.TO_VALIDATE //At the moment we only allow this status update
          })
          .then((response) => response);
          uiStore.showMessage("success", response.message || "Status da reserva atualizado com sucesso!");
          return response;
      } catch (error: any) {
        uiStore.showMessage(
          "error",
          error.message || "Erro ao atualizar o status da reserva."
        );
        throw error;
      } finally {
        uiStore.stopLoading();
      }
    },
      async importDataForm(actualGuestId: number, selectedGuestId: number, code: string, isGoingToCurrentReservation: OptionChoice, lang: LangType) {
      const uiStore = useUIStore();
      try {
        const response = await fetchWrapper.post(
          `/api/stays/reservations/import-data-form`,
          { actualGuestId, selectedGuestId, code, isGoingToCurrentReservation, lang }
        );
        return response;
      } catch (error: any) {
        uiStore.showMessage("error", error.message || "Erro ao importar dados da reserva.");
        throw error;
      }
    },

}});
